// SAFETY CONTROL. Read before changing anything here.
//
// Stripe collects the money; SSM only records that it happened. If a request
// we send ever carries real payment instructions, SSM runs them through the
// FACILITY's gateway and the customer is charged twice — once by us, once by
// them. AutoPay would do the same thing every month, and OverrideUnitStatus
// would let a move-in land on an occupied unit, destroying the duplicate
// protection the whole payment design leans on.
//
// This file enforces that by inspecting the finished request body. It runs at
// the end of every builder in map.ts AND again immediately before transport, so
// no code path can assemble a body that escapes it.

import { PmsGuardError } from '../../errors';
import type { PmsRequestEnvelope, SsmPaymentMethod } from '../../types';

/** The only payment methods we may send. Both are "record only", no gateway. */
export const ALLOWED_PAYMENT_METHODS: readonly SsmPaymentMethod[] = ['cash', 'check'];

/**
 * Objects that carry card/bank instructions or tokens. Matched
 * case-insensitively so `CreditCardInput` and `achinput` are caught too.
 */
export const FORBIDDEN_KEYS: readonly string[] = [
  'creditcardinput',
  'achinput',
  'eftinput',
  'creditcardautopayinput',
];

/** Keys that imply raw card data regardless of where they sit. */
const CARD_DATA_KEY = /cvv|cardnumber|ccnumber|expir|trackdata|routingnumber|accountnumber/i;

/** A bare 13-19 digit string is PAN-shaped — the `1234123412341234` class of bug. */
const PAN_SHAPED = /^\d{13,19}$/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Deep-walks a request body and returns the JSON path of every violation.
 * An empty array means the body is safe to send.
 */
export function findForbidden(body: unknown, path = '$'): string[] {
  const violations: string[] = [];

  if (Array.isArray(body)) {
    body.forEach((item, index) => {
      violations.push(...findForbidden(item, `${path}[${index}]`));
    });
    return violations;
  }

  if (!isPlainObject(body)) {
    if (typeof body === 'string' && PAN_SHAPED.test(body)) {
      violations.push(`${path} (card-number-shaped value)`);
    }
    return violations;
  }

  Object.keys(body).forEach((key) => {
    const value = body[key];
    const here = `${path}.${key}`;
    const lowered = key.toLowerCase();

    if (FORBIDDEN_KEYS.indexOf(lowered) !== -1) {
      // A present-but-null key is fine: it serializes to nothing actionable.
      if (value !== null && value !== undefined) {
        violations.push(`${here} (payment instrument)`);
      }
    }

    if (CARD_DATA_KEY.test(key) && value !== null && value !== undefined) {
      violations.push(`${here} (card data)`);
    }

    if (lowered === 'paymentmethod') {
      const normalized = String(value == null ? '' : value).trim().toLowerCase();
      if (ALLOWED_PAYMENT_METHODS.indexOf(normalized as SsmPaymentMethod) === -1) {
        violations.push(`${here} (payment method "${String(value)}")`);
      }
    }

    if (lowered === 'autopay' && value === true) {
      violations.push(`${here} (autopay enables vendor-side recurring billing)`);
    }

    if (lowered === 'overrideunitstatus' && value === true) {
      violations.push(`${here} (would allow move-in onto an occupied unit)`);
    }

    violations.push(...findForbidden(value, here));
  });

  return violations;
}

/** Throws PmsGuardError if the envelope's body is unsafe. */
export function assertSafeSsmRequest(envelope: PmsRequestEnvelope): void {
  const violations = findForbidden(envelope.body);
  if (violations.length > 0) {
    throw new PmsGuardError(violations);
  }
}
